import type { BomTreeNode, FlatPart } from './boms';

/** A cell value written to CSV or to a Google Sheet. */
export type SheetCell = string | number;

export interface SheetTable {
  /** Tab name. Google Sheets forbids `\ / ? * [ ] :` in a tab title. */
  title: string;
  /** First row is the header. */
  rows: SheetCell[][];
}

const centsToDollars = (cents: number) => (cents / 100).toFixed(2);

/**
 * Default file title. Build quantity is part of the name when the sheet
 * was scaled to something other than one unit, so two exports don't look
 * identical in Drive.
 */
export function bomSheetTitle(sku: string, buildQty: number): string {
  const qty =
    buildQty === 1 ? '' : ` x ${buildQty.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
  return `${sku} BOM${qty}`.slice(0, 200);
}

/**
 * The two tabs we publish: the exploded multi-level BOM, and the rolled-up
 * pick list. Same columns as the CSV download.
 */
export function bomExportTables(nodes: BomTreeNode[], parts: FlatPart[]): SheetTable[] {
  return [
    {
      title: 'BOM',
      rows: [
        [
          'Level',
          '#',
          'SKU',
          'Component',
          'Sub-assembly',
          'Qty / unit',
          'UoM',
          'Scrap %',
          'Ext. qty',
          'Unit cost',
          'Ext. cost',
          'Notes',
        ],
        ...nodes.map((n) => [
          n.depth + 1,
          n.path,
          n.line.item?.sku ?? '',
          n.line.item?.name ?? '',
          n.isSubAssembly ? 'yes' : '',
          n.line.quantity,
          n.line.uom,
          n.line.scrap_pct,
          n.extQty,
          n.unitCost.missing ? '' : centsToDollars(n.unitCost.cents),
          n.unitCost.missing ? '' : centsToDollars(n.extCostCents),
          n.line.notes ?? '',
        ]),
      ],
    },
    {
      title: 'Parts list',
      rows: [
        ['SKU', 'Part', 'Pulled into', 'Total qty', 'UoM', 'Unit cost', 'Ext. cost'],
        ...parts.map((p) => [
          p.item?.sku ?? '',
          p.item?.name ?? '',
          p.usedUnder.join('; '),
          p.totalQty,
          p.uom,
          p.unitCostCents == null ? '' : centsToDollars(p.unitCostCents),
          p.unitCostCents == null ? '' : centsToDollars(p.extCostCents),
        ]),
      ],
    },
  ];
}

/** Strip characters Google rejects in a sheet tab name. */
export function safeSheetTitle(title: string, fallback: string): string {
  const cleaned = title
    .replace(/[\\/?*[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return (cleaned || fallback).slice(0, 100);
}

/** Quote a tab name for an A1 range (`'Parts list'!A1`). */
export function quoteSheetName(title: string): string {
  return `'${title.replace(/'/g, "''")}'`;
}

export function sheetRowsToCsv(rows: SheetCell[][]): string {
  return rows
    .map((r) =>
      r
        .map((cell) => {
          const s = String(cell ?? '');
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    )
    .join('\n');
}

export function downloadCsv(filename: string, rows: SheetCell[][]) {
  const url = URL.createObjectURL(
    new Blob([sheetRowsToCsv(rows)], { type: 'text/csv;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
